import axios from 'axios';

const BASE_URL = 'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php';
const EXTRANET_URL = 'https://api.tabtrips.com/olivrweb/hotelapi/extranet.php';
const GST_VERIFY_API = 'https://gstverify.co.in/api/v1/verify';
const GST_API_KEY = 'gstv_dcab8e56b92ee4be1dad77bb896e6c8b81aadf6483c67ff9';

/**
 * Verify a GST number via gstverify.co.in
 * @param {string} gstNumber - 15-digit GSTIN
 * @returns {Promise<Object>} { success, data: { legal_name, trade_name, status, constitution, state, district, pincode, pan, ... } }
 */
export const verifyGst = async (gstNumber) => {
  const gstin = String(gstNumber || '').trim().toUpperCase();
  if (!gstin) {
    return { success: false, message: 'Please enter a GST number' };
  }
  try {
    const response = await axios.get(`${GST_VERIFY_API}/${gstin}`, {
      headers: { 'X-API-Key': GST_API_KEY },
    });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || error.response?.data?.error || 'Failed to verify GST number';
    return { success: false, message };
  }
};

/**
 * Send login OTP to the user's email
 * @param {string} email - User's email address
 * @returns {Promise<Object>} API response
 */
export const sendLoginOtp = async (email) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/login`,
      { method: 'otp', email },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return response.data;
  } catch (error) {
    console.error('Send Login OTP Error:', error);
    throw error;
  }
};

/**
 * Login with OTP
 * @param {Object} payload - { email, otp }
 * @returns {Promise<Object>} API response
 */
export const loginWithOtp = async ({ email, otp }) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/login`,
      { method: 'otp', email, otp },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return response.data;
  } catch (error) {
    console.error('Login With OTP Error:', error);
    throw error;
  }
};

/**
 * Login with Google ID token
 * @param {string} idToken - Google ID token (JWT) from Google Identity Services
 * @returns {Promise<Object>} API response
 */
export const loginByGoogle = async (idToken) => {
  const response = await axios.post(
    `${BASE_URL}/loginByGoogle`,
    { idToken },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return response.data;
};

/**
 * Change password handler function
 * @param {Object} payload - { token, user_id, old_password, new_password, confirm_password }
 * @returns {Promise<Object>} API response
 */
export const change_pass = async (payload) => {
  try {
    const { token, ...body } = payload;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await axios.post(`${BASE_URL}/changePassword`, { ...body, token }, {
      headers,
    });

    return response.data;
  } catch (error) {
    console.error('Change Password Error:', error);
    throw error;
  }
};

/**
 * Generate OTP handler function
 * @param {string} email - User's email address
 * @returns {Promise<Object>} API response
 */
export const generate_otp = async (email) => {
  try {
    const formData = new FormData();
    formData.append('email', email);

    const response = await axios.post(`${BASE_URL}/generateOTP`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    return response.data;
  } catch (error) {
    console.error('Generate OTP Error:', error);
    throw error;
  }
};

/**
 * Verify OTP handler function
 * @param {Object} payload - { email, otp }
 * @returns {Promise<Object>} API response
 */
export const verify_otp = async (payload) => {
  try {
    const formData = new FormData();
    formData.append('email', payload.email);
    formData.append('otp', payload.otp);

    const response = await axios.post(`${BASE_URL}/verifyOtp`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    return response.data;
  } catch (error) {
    console.error('Verify OTP Error:', error);
    throw error;
  }
};

/**
 * Sign up a new host account via the standalone extranet API.
 * @param {Object} payload - { name, email, phone, password, confirmPassword, gender }
 * @returns {Promise<Object>} API response
 */
export const signupHost = async (payload) => {
  try {
    const formData = new FormData();
    formData.append('name', payload.name || '');
    formData.append('email', payload.email || '');
    formData.append('phone', payload.phone || '');
    formData.append('password', payload.password || '');
    formData.append('confirmPassword', payload.confirmPassword || '');
    formData.append('gender', payload.gender || 'other');

    const response = await axios.post(`${EXTRANET_URL}/signup`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Signup Error:', error);
    throw error;
  }
};

/**
 * Send public signup OTP for email or phone.
 * @param {Object} payload - { identifier, type: 'email'|'phone' }
 * @returns {Promise<Object>} API response
 */
export const sendSignupOtp = async (payload) => {
  try {
    const formData = new FormData();
    formData.append('identifier', payload.identifier || '');
    formData.append('type', payload.type || 'email');

    const response = await axios.post(`${EXTRANET_URL}/sendOtp`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Send Signup OTP Error:', error);
    throw error;
  }
};

/**
 * Verify public signup OTP.
 * @param {Object} payload - { identifier, type: 'email'|'phone', otp }
 * @returns {Promise<Object>} API response
 */
export const verifySignupOtp = async (payload) => {
  try {
    const formData = new FormData();
    formData.append('identifier', payload.identifier || '');
    formData.append('type', payload.type || 'email');
    formData.append('otp', payload.otp || '');

    const response = await axios.post(`${EXTRANET_URL}/verifyOtp`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Verify Signup OTP Error:', error);
    throw error;
  }
};

/**
 * Reset password handler function
 * @param {Object} payload - { email, new_password, confirm_password }
 * @returns {Promise<Object>} API response
 */
export const reset_password = async (payload) => {
  try {
    const formData = new FormData();
    formData.append('email', payload.email);
    formData.append('new_password', payload.new_password);
    formData.append('confirm_password', payload.confirm_password);

    const response = await axios.post(`${BASE_URL}/resetPassword`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    return response.data;
  } catch (error) {
    console.error('Reset Password Error:', error);
    throw error;
  }
};

/**
 * Get account details handler function
 * @param {Object} payload - { hotel_id, token }
 * @returns {Promise<Object>} API response
 */
export const account = async (payload) => {
  try {
    const response = await axios.post(`${BASE_URL}/getAccountDetails`, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    return response.data;
  } catch (error) {
    console.error('Get Account Details Error:', error);
    throw error;
  }
};

/**
 * Update account details handler function
 * @param {Object} payload - { hotel_id, token, account_holder_name, account_number, bank_name, ifsc_code, pan_number, gst_number, postal_code }
 * @returns {Promise<Object>} API response
 */
export const updateAccount = async (payload) => {
  try {
    const response = await axios.post(`${BASE_URL}/updateAccountDetails`, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    return response.data;
  } catch (error) {
    console.error('Update Account Details Error:', error);
    throw error;
  }
};